import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/en'
import 'dayjs/locale/sv'
import 'dayjs/locale/nb'
import { DropinTicket, DropinTicketLog } from '../../../../pure-js/types/GrooverTypes'

dayjs.extend(relativeTime)

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export const isExpiringWithin30Days = (validUntil: string): boolean => {
  const valid = new Date(validUntil)
  const now = new Date()
  const daysUntil = Math.ceil((valid.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  return daysUntil > 0 && daysUntil <= 30
}

export const formatPrice = (priceInCents: number, currency?: string): string => {
  const price = priceInCents / 100
  return `${price.toFixed(2)} ${currency || 'SEK'}`
}

/** Match dancer: sort by most clips left first, then tickets with clips before empty ones */
export const sortDropinTicketsByClips = (list: DropinTicket[]): DropinTicket[] => {
  return [...list].sort((a, b) => {
    const clipsLeftA = (a.ticketCount || 0) - (a.ticketsUsed || 0)
    const clipsLeftB = (b.ticketCount || 0) - (b.ticketsUsed || 0)
    if (clipsLeftA > 0 && clipsLeftB > 0) return clipsLeftB - clipsLeftA
    if (clipsLeftA > 0 && clipsLeftB === 0) return -1
    if (clipsLeftA === 0 && clipsLeftB > 0) return 1
    return 0
  })
}

/** Match dancer: deleted tickets show "Active" label (dancer has no deleted case, defaults to active) */
export const STATUS_KEYS: Record<string, string> = {
  active: 'portalDropinTicketStatusActive',
  pending_payment: 'portalDropinTicketStatusPendingPayment',
  expired: 'portalDropinTicketStatusExpired',
  usedUp: 'portalDropinTicketStatusUsedUp',
  refunded: 'portalDropinTicketStatusRefunded',
  deleted: 'portalDropinTicketStatusActive'
}

export const getStatusLabel = (status: string, getText: (key: string) => string): string => {
  const key = STATUS_KEYS[status]
  return key ? getText(key) : status
}

export const getLastConsumeLog = (ticket: DropinTicket): DropinTicketLog | null => {
  const consumeLogs = (ticket.logs || []).filter((log) => log.type === 'consume')
  if (consumeLogs.length === 0) return null
  const sorted = [...consumeLogs].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
  return sorted[0] ?? null
}

export const isRecentlyActivated = (log: DropinTicketLog, withinMinutes = 60): boolean => {
  const logTime = new Date(log.timestamp).getTime()
  const now = Date.now()
  const diffMs = now - logTime
  const diffMinutes = diffMs / (1000 * 60)
  return diffMinutes >= 0 && diffMinutes <= withinMinutes
}

export const formatMinutesAgo = (timestamp: string, locale: string): string => {
  const dayjsLocale = locale === 'no' ? 'nb' : locale
  return dayjs(timestamp).locale(dayjsLocale).fromNow()
}
